"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { debounce, flatMap, uniqBy } from "lodash-es";
import {
	ArrowDown,
	ArrowUp,
	ChevronDown,
	Info,
	Minus,
	Plus,
	Search,
	SlidersHorizontal,
	X,
	XCircle,
} from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import * as React from "react";
import { AccessRules, Page } from "types";

import { AccessCheck } from "@/components/access-check";
import { ExaminePlusLink, FlashSaleCTA, OverlayCta, WhatWorksCta } from "@/components/cta";
import DatabaseFilter, { defaulFiltertValues } from "@/components/edb-filter";
import parse from "@/components/html-parser";
import ChevronToggle from "@/components/icons/chevron-toggle";
import Doc from "@/components/icons/doc";
import DownMinorArrow from "@/components/icons/down-minor-arrow";
import DownNotableArrow from "@/components/icons/down-notable-arrow";
import DownStrongArrow from "@/components/icons/down-strong-arrow";
import MixedEffectArrow from "@/components/icons/mixed-effect-arrow";
import NoEffectArrow from "@/components/icons/no-effect-arrow";
import Sort from "@/components/icons/sort";
import UpMinorArrow from "@/components/icons/up-minor-arrow";
import UpNotableArrow from "@/components/icons/up-notable-arrow";
import UpStrongArrow from "@/components/icons/up-strong-arrow";
import { User2 as User } from "@/components/icons/user";
import { LockBadge } from "@/components/lock";
import PaidPill from "@/components/paid-pill";
import SaleWrapper from "@/components/sale-wrapper";
import { StudySidebar } from "@/components/studies-sidebar";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { trackAdClick } from "@/lib/analytics-tracking";
import { salesId, salesYear } from "@/lib/data/sales";
import { useAccess } from "@/lib/hooks/use-access";
import { useTrackEdbElement } from "@/lib/hooks/use-track-edb-element";
import { cn, updateUrlParam } from "@/lib/utils";

const tableColumns = {
	intervention: [
		{
			title: "Health Condition/Goal",
			rule: "free",
			sort: "condition",
			content: `The health condition or goal that was studied.`,
		},
		{
			title: "Health Outcome",
			rule: "free",
			sort: "name",
			content: `The specific, measurable endpoints that were studied.`,
		},
		{
			title: "Grade",
			rule: "paid",
			sort: "grade",
			content: `The grade summarizes how potentially beneficial the intervention is for a given outcome.
     It’s based on a formula that incorporates the number of trials, consistency of evidence, and effect sizes.
     The grade is subject to change as the evidence evolves and as we update and improve our analyses.`,
		},
		{
			title: "Evidence",
			rule: "paid",
			sort: "total_studies",
			direction: "DESC",
			content: `The number of randomized controlled trials as well as the total number of participants in those trials.`,
		},
		{
			title: "Effect",
			rule: "paid",
			sort: "arrow",
			direction: "DESC",
			content: `How big the change was, when summarized across trials.`,
		},
	],
	topic: [
		{
			title: "Intervention",
			rule: "free",
			sort: "intervention",
			content: `What was tested (like a supplement, diet, or lifestyle intervention).`,
		},
		{
			title: "Health Outcome",
			rule: "free",
			sort: "name",
			content: `The specific, measurable endpoints that were studied.`,
		},
		{
			title: "Grade",
			rule: "paid",
			sort: "grade",
			content: `The grade summarizes how potentially beneficial the intervention is for a given outcome.
     It’s based on a formula that incorporates the number of trials, consistency of evidence, and effect sizes.
     The grade is subject to change as the evidence evolves and as we update and improve our analyses.`,
		},
		{
			title: "Evidence",
			rule: "paid",
			sort: "total_studies",
			direction: "DESC",
			content: `The number of randomized controlled trials as well as the total number of participants in those trials.`,
		},
		{
			title: "Effect",
			rule: "paid",
			sort: "arrow",
			direction: "DESC",
			content: `How big the change was, when summarized across trials.`,
		},
	],
	outcomes: [
		{
			title: "Intervention",
			rule: "free",
			sort: "name",
			content: `What was tested (like a supplement, diet, or lifestyle intervention).`,
		},
		{
			title: "Grade",
			rule: "paid",
			sort: "grade",
			content: `The grade summarizes how potentially beneficial the intervention is for a given outcome.
     It’s based on a formula that incorporates the number of trials, consistency of evidence, and effect sizes.
     The grade is subject to change as the evidence evolves and as we update and improve our analyses.`,
		},
		{
			title: "Evidence",
			rule: "paid",
			sort: "total_studies",
			direction: "DESC",
			content: `The number of randomized controlled trials as well as the total number of participants in those trials.`,
		},
		{
			title: "Effect",
			rule: "paid",
			sort: "arrow",
			direction: "DESC",
			content: `How big the change was, when summarized across trials.`,
		},
	],
};

const mobileTableColumns = {
	interventionOrTopic: [
		{
			title: "Health Outcome",
			rule: "free",
			content: `The specific, measurable endpoints that were studied.`,
		},
		{
			title: "Grade",
			rule: "paid",
			content: `The grade summarizes how potentially beneficial the intervention is for a given outcome.
     It’s based on a formula that incorporates the number of trials, consistency of evidence, and effect sizes.
     The grade is subject to change as the evidence evolves and as we update and improve our analyses.`,
		},
		{
			title: "Effect",
			rule: "paid",
			direction: "DESC",
			content: `How big the change was, when summarized across trials.`,
		},
		{ title: "Detail", rule: "paid", content: "The study details" },
	],
	outcomes: [
		{
			title: "Intervention",
			rule: "free",
			content: `What was tested (like a supplement, diet, or lifestyle intervention).`,
		},
		{
			title: "Grade",
			rule: "paid",
			content: `The grade summarizes how potentially beneficial the intervention is for a given outcome.
     It’s based on a formula that incorporates the number of trials, consistency of evidence, and effect sizes.
     The grade is subject to change as the evidence evolves and as we update and improve our analyses.`,
		},
		{
			title: "Effect",
			rule: "paid",
			direction: "DESC",
			content: `How big the change was, when summarized across trials.`,
		},
		{ title: "Detail", rule: "paid", content: "The study details" },
	],
};

const gradeVariants = cva(
	"h-10 w-10 shrink-0 cursor-pointer rounded text-center font-bold uppercase leading-10 text-white drop-shadow-md select-none",
	{
		variants: {
			variant: {
				a: "bg-grade-a-top",
				b: "bg-grade-b-top",
				c: "bg-grade-c-top",
				d: "bg-grade-d-top",
				f: "bg-grade-f-top",
			},
		},
	}
);

const fiveColGrid = "!grid-cols-[1fr_1fr_min(120px)_minmax(140px,180px)_min(120px)]";
const fourColGrid = "!grid-cols-[1fr_min(100px)_minmax(140px,180px)_min(120px)]";

const sendFreeAccountClickEvent = ({ isLoggedIn }: { isLoggedIn?: boolean }) => {
	if (isLoggedIn) return;

	window.dataLayer = window.dataLayer || [];
	window.dataLayer?.push({
		event: "edb_alertbox_click",
		label: "free_account",
	});
};

const sendPaidSubscriptionClickEvent = ({
	isLoggedIn,
	hasAccess,
}: {
	isLoggedIn?: boolean;
	hasAccess?: boolean;
}) => {
	if (isLoggedIn && hasAccess) {
		return;
	}

	window.dataLayer = window.dataLayer || [];
	window.dataLayer?.push({
		event: "edb_alertbox_click",
		label: "paid_subscription",
	});
};

const sendEdbLockClickEvent = () => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer?.push({
		event: "edb_locked_icon_click",
	});
};

const sendViewSlideoutEvent = () => {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer?.push({
		event: "edb_slideout_view",
	});
};

interface GradeProps extends VariantProps<typeof gradeVariants> {
	entry: Entry;
	study: any;
	interventionOrTopic: any;
}

export function Grade({ entry, interventionOrTopic }: any) {
	if (!entry?.showContent) {
		return (
			<EDBSlideout>
				<div className="flex items-center justify-center">
					<LockBadge
						tooltip={<OverlayCta trackEdbSignup />}
						size={10}
						trackEvent={sendEdbLockClickEvent}
					/>
				</div>
			</EDBSlideout>
		);
	}

	return (
		<div className="flex items-center justify-center">
			<Tooltip show={!!entry?.notes}>
				<TooltipTrigger asChild>
					<span className={cn(gradeVariants({ variant: entry?.grade?.toLowerCase() }))}>
						{entry?.grade}
					</span>
				</TooltipTrigger>
				<TooltipContent variant="overview">
					{entry.notes ? (
						<div className="grade-overview-tooltip p-0">
							<h6 className="grade-headings">
								Summary of{" "}
								{interventionOrTopic?.name && (
									<>
										<span className="capitalize">{interventionOrTopic?.name}</span> and
									</>
								)}{" "}
								<span className="capitalize">{entry?.name}</span>
							</h6>
							<div>
								{entry?.showTooltip ? (
									parse(entry?.notes)
								) : (
									<span className={`group flex items-center `}>
										<EDBSlideout>
											<div className="flex items-center justify-center">
												<LockBadge
													size={10}
													className="mr-5 inline-block"
													trackEvent={sendEdbLockClickEvent}
												/>
											</div>
										</EDBSlideout>
										{/* TODO: TRACK CLICKS */}
										<OverlayCta trackEdbSignup className="!p-0" />
									</span>
								)}
							</div>
						</div>
					) : null}
				</TooltipContent>
			</Tooltip>
		</div>
	);
}
interface ArrowProps {
	text: string;
	icon: string[];
}
interface EffectArrowProps {
	arrow: ArrowProps;
}

export const getArrowScore = (arrow: string[]): number => {
	if (!arrow.length) {
		return 0;
	} else if (arrow.length > 1) {
		return -1;
	} else {
		const arr = arrow[0].split("-");
		const count = parseInt(arr.pop() || "0", 10);
		const direction = arr.pop();
		if (direction === "up") {
			return count;
		} else {
			return -Math.abs(count + 1);
		}
	}
};

export function EffectArrow({ arrow }: EffectArrowProps) {
	const getShade = React.useMemo(() => {
		if (arrow?.text?.toLowerCase().includes("improvement")) {
			return "#22c55e";
		} else if (arrow.text.toLowerCase().includes("detriment")) {
			return "#ef4444";
		}
		return "#6C757D";
	}, [arrow]);

	const getArrowComponent = React.useMemo(() => {
		const commonProps = {
			shade: getShade,
			width: 30,
			height: 30,
			alt: "up-arrow",
		};

		switch (arrow?.icon[0]) {
			case "icon-up-3":
				return <UpStrongArrow {...commonProps} />;
			case "icon-down-3":
				return <DownStrongArrow {...commonProps} />;
			case "icon-down-2":
				return <DownNotableArrow {...commonProps} />;
			case "icon-up-2":
				return <UpNotableArrow {...commonProps} />;
			case "icon-down-1":
				return <DownMinorArrow {...commonProps} />;
			case "icon-up-1":
				return <UpMinorArrow {...commonProps} />;
			case "icon-up":
			case "icon-down":
				return <MixedEffectArrow {...commonProps} width={40} height={15} />;
			default:
				return <NoEffectArrow {...commonProps} className="h-1 w-10" />;
		}
	}, [arrow, getShade]);

	return getArrowComponent;
}

type Adjectives = {
	small: string;
	moderate: string;
	large: string;
};

type Arrow = {
	text: string;
	icon: string[];
};

const adjectives: Adjectives = {
	small: "slightly",
	moderate: "moderately",
	large: "strongly",
};

interface EffectProps extends React.HTMLAttributes<HTMLDivElement> {
	entry: Entry;
	study: any;
	interventionOrTopic: any;
	showText?: boolean;
}

export function Effect({ entry, showText = true, className }: EffectProps) {
	const { arrow, name, showContent } = entry;

	const createEffectText = React.useCallback((arrow: Arrow, outcome: any) => {
		const effect = arrow?.text?.toLowerCase();
		const direction = arrow?.icon[0]?.includes("up") ? "increased" : "decreased";
		const degree = arrow?.text?.split(" ")[0].toLowerCase();

		const isMixedEffect = effect === "mixed effect";
		const hasImprovementOrDetriment =
			effect?.includes("improvement") || effect?.includes("detriment");

		if (isMixedEffect) {
			return `The results of this intervention show mixed effects on ${outcome}.`;
		}

		if (!hasImprovementOrDetriment) {
			return `${outcome} ${
				adjectives[degree as keyof Adjectives]
			} ${direction}, but not enough to provide a benefit or result in a detriment.`;
		}

		return `${outcome} ${
			adjectives[degree as keyof Adjectives]
		} ${direction}, indicating a ${effect}.`;
	}, []);

	const effectText = React.useMemo(
		() => createEffectText(arrow, name),
		[arrow, name, createEffectText]
	);

	if (!showContent) {
		return (
			<EDBSlideout>
				<div className="flex items-center justify-center">
					<LockBadge
						tooltip={<OverlayCta trackEdbSignup />}
						size={10}
						trackEvent={sendEdbLockClickEvent}
					/>
				</div>
			</EDBSlideout>
		);
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<div className={cn("flex flex-col items-center justify-center gap-1", className)}>
					<EffectArrow arrow={arrow} />
					{showText && <span>{arrow?.text}</span>}
				</div>
			</TooltipTrigger>
			<TooltipContent>
				<div className="p-3">
					{effectText}
					<span className="my-3 block border-t border-gray-300" />
					<ul className={cn("flex flex-col gap-1 text-sm text-gray-600")}>
						<li
							className={cn(
								"flex items-center gap-2",
								arrow?.text.toLowerCase().includes("improvement")
									? "font-medium text-[#22c55e]"
									: "opacity-60"
							)}
						>
							<span className="size-2 rounded-full bg-[#22c55e]" />
							Green indicates a benefit
						</li>
						<li
							className={cn(
								"flex items-center gap-2",
								arrow?.text.toLowerCase().includes("detriment")
									? "font-medium text-[#ef4444]"
									: "opacity-60"
							)}
						>
							<span className="size-2 rounded-full bg-[#ef4444]" />
							Red indicates harm
						</li>
						<li
							className={cn(
								"flex items-center gap-2",
								/increase|decrease/i.test(arrow?.text) ? "font-medium text-[#6C757D]" : "opacity-60"
							)}
						>
							<span className="size-2 rounded-full bg-[#6C757D]" />
							Gray is mixed or neutral
						</li>
					</ul>
				</div>
			</TooltipContent>
		</Tooltip>
	);
}
interface Study {
	id: number;
	meta_analysis_data: object | null;
}
interface Entry {
	name: string;
	grade: string;
	consensus?: {
		label: string;
		studies: number;
		value: number;
	};
	samplesize: number;
	arrow: ArrowProps;
	id: string;
	url: string;
	slug: string;
	tooltip: string;
	showContent?: boolean;
	showTooltip?: boolean;
	total_studies?: number;
	studies: Study[];
}

interface DatabaseEntriesProps {
	entries: Entry[];
	type: "mobile" | "desktop";
	accessRules: AccessRules;
}

const DatabaseEntries = ({
	study,
	interventionOrTopic,
	type,
	showConditions = true,
	accessRules,
	pageType,
}: any) => {
	if (!study.entries?.length) return;

	if (type === "mobile") {
		return (
			<Table className="border-none">
				<TableHeader className="text-menu">
					<TableRow>
						{mobileTableColumns[pageType === "outcomes" ? "outcomes" : "interventionOrTopic"].map(
							(col) => (
								<Tooltip key={col.title} placement="bottom">
									<TooltipTrigger asChild>
										<TableHead
											key={col.title}
											className="flex-1 cursor-pointer border-none p-2 text-left text-base font-bold"
										>
											{col.title}
										</TableHead>
									</TooltipTrigger>
									<TooltipContent variant="purple" className="p-4 text-base">
										{parse(col?.content)}
									</TooltipContent>
								</Tooltip>
							)
						)}
					</TableRow>
				</TableHeader>
				<TableBody className="divide-none">
					{study?.entries?.map((entry: Entry, id: React.Key) => (
						<TableRow
							className={cn(!entry?.showContent ? "cursor-pointer" : "")}
							key={`${entry?.id}${id}`}
						>
							<TableCell className="p-2">
								<span className="inline-block">
									<a
										data-placement="top"
										className="font-semibold text-primary hover:underline md:font-normal"
										href={entry?.url}
									>
										<span className="block capitalize">{entry?.name}</span>
									</a>
								</span>
							</TableCell>

							<TableCell className="p-2">
								<Grade entry={entry} study={study} interventionOrTopic={interventionOrTopic} />
							</TableCell>

							<TableCell className="p-2">
								{entry && (
									<span className="inline-block rounded hover:bg-examine-purple-200">
										<Effect
											entry={entry}
											study={study}
											interventionOrTopic={interventionOrTopic}
											className="size-10 flex-row justify-center gap-x-2 [&>span]:px-3 [&>span]:font-semibold"
											showText={false}
										/>
									</span>
								)}
							</TableCell>

							<TableCell className="p-2">
								<StudySidebar
									page={interventionOrTopic}
									parent={study}
									entry={entry}
									accessRules={accessRules}
								>
									<span className="flex size-10 items-center justify-center rounded hover:bg-examine-purple-200">
										<Info strokeWidth={1.5} size={30} className="text-[#6e488c]" />
									</span>
								</StudySidebar>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		);
	}

	return study?.entries?.map((entry: Entry, id: React.Key) => (
		<TableRow
			className={cn(
				"hover:bg-examine-purple-100",
				showConditions ? fiveColGrid : fourColGrid,
				!entry?.showContent ? "cursor-pointer" : ""
			)}
			key={`${entry?.id}${id}`}
		>
			{showConditions && <TableCell></TableCell>}
			<TableCell>
				<div className="text-center text-[15px]">
					<Link
						href={entry?.url}
						className="font-semibold text-primary hover:underline md:font-normal"
					>
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="block capitalize">{entry?.name}</span>
							</TooltipTrigger>
							<TooltipContent variant="overview">{parse(entry?.tooltip)}</TooltipContent>
						</Tooltip>
					</Link>
				</div>
			</TableCell>
			<TableCell className="text-center">
				<Grade entry={entry} study={study} interventionOrTopic={interventionOrTopic} />
			</TableCell>
			<TableCell className="text-center">
				<StudySidebar
					page={interventionOrTopic}
					parent={study}
					entry={entry}
					accessRules={accessRules}
				>
					<div className="flex items-center justify-center">
						<div className="flex flex-col items-start gap-2 text-center">
							<Button variant="text" size="sm" className="pl-0 font-medium underline">
								<Doc className="h-4 text-examine-purple-600/50" />
								{entry?.total_studies} {entry?.total_studies > 1 ? "Studies" : "Study"}
							</Button>
							<p className="flex items-center gap-1.5 text-13 font-medium text-examine-purple-500">
								<span className="w-5 shrink-0">
									<User className="mx-auto size-4" />
								</span>
								<span className="text-start text-sm">Participants: {entry?.samplesize}</span>
							</p>
						</div>
					</div>
				</StudySidebar>
			</TableCell>
			<TableCell className="text-center text-13">
				{entry && <Effect entry={entry} study={study} interventionOrTopic={interventionOrTopic} />}
			</TableCell>
		</TableRow>
	));
};

interface DatabaseRowProps {
	study: {
		condition: {
			name: string;
			url: string;
		};
		total_entries: number;
		total_studies: number;
		entries: Entry[];
	};
	interventionOrTopic: {
		name: string;
		slug: string;
	};
	toggle: boolean;
	accessRules: AccessRules;
}

const DatabaseRow = ({
	study,
	interventionOrTopic,
	toggle,
	accessRules,
	viewport,
	pageType,
}: any) => {
	const [showEntries, setShowEntries] = React.useState<boolean>(false);

	React.useEffect(() => {
		setShowEntries(toggle);
	}, [toggle]);

	if (viewport === "mobile") {
		return (
			<Collapsible
				className="flex flex-col gap-1 overflow-hidden rounded-md border border-examine-purple-200 bg-examine-purple-100/50 [&[data-state=open]svg.minus]:block [&[data-state=open]svg.plus]:hidden"
				defaultOpen={toggle}
				onOpenChange={(isOpen) => setShowEntries(isOpen)}
				open={showEntries}
			>
				<div className="bg-primary py-2 text-center text-lg font-semibold text-white">
					<Link href={study?.condition?.url || study?.intervention?.url}>
						{study?.condition?.name || study?.intervention?.name}
					</Link>
				</div>
				<div className="mx-4 my-6">
					<CollapsibleTrigger className="w-full rounded border border-dashed border-primary bg-info py-3 text-center text-primary">
						<span className="mr-1 text-sm font-normal md:text-base">
							{study.total_entries} {study.total_entries > 1 ? "Outcomes" : "Outcome"}
						</span>
						{study.total_entries && (
							<span className="-mb-1 ml-2 inline-block rounded border border-primary p-0.5 py-0 md:hidden md:border-gray-600">
								{showEntries ? (
									<Minus size={16} className="w-3" />
								) : (
									<Plus size={16} className="w-3" />
								)}
							</span>
						)}
					</CollapsibleTrigger>
					<CollapsibleContent className="mt-4">
						<DatabaseEntries
							study={study}
							interventionOrTopic={interventionOrTopic}
							accessRules={accessRules}
							type="mobile"
							pageType={pageType}
						/>
					</CollapsibleContent>
				</div>
			</Collapsible>
		);
	}

	return (
		<>
			<TableRow
				onClick={() => setShowEntries((currentValue) => !currentValue)}
				className={cn(
					"grid cursor-pointer border bg-main hover:bg-[#f8f8f9] md:h-24 md:divide-x md:divide-gray-200",
					fiveColGrid
				)}
			>
				<TableCell className="justify-start border-examine-purple-100 md:px-6 md:pr-0">
					<div className="justify-left flex items-center">
						<Link
							href={study?.condition?.url || study?.intervention?.url}
							className="font-semibold text-primary hover:underline md:font-normal"
						>
							<Tooltip>
								<TooltipTrigger asChild>
									<span>{study?.condition?.name || study?.intervention?.name}</span>
								</TooltipTrigger>
								<TooltipContent variant="overview">
									{parse(study?.condition?.tooltip || study?.intervention?.tooltip)}
								</TooltipContent>
							</Tooltip>
						</Link>
						{Boolean(study.total_entries) && (
							<Button variant="text" className="hover:bg-transparent active:bg-transparent">
								<ChevronToggle className={cn(showEntries ? "rotate-90" : "")} />
							</Button>
						)}
					</div>
				</TableCell>
				<TableCell className="border-examine-purple-100">
					<div className="text-center">
						{study.total_entries} {study.total_entries > 1 ? "Outcomes" : "Outcome"}
					</div>
				</TableCell>
				<TableCell></TableCell>
				<TableCell className="font-medium text-primary">
					<StudySidebar page={interventionOrTopic} parent={study} accessRules={accessRules}>
						<div>
							<div className="flex items-center justify-start gap-1.5">
								<Doc className="h-4 text-examine-purple-600/50" />
								<span className="border-b border-primary text-center text-sm -tracking-1">
									{study?.total_studies} {study?.total_studies === 1 ? "Study" : "Studies"}
								</span>
							</div>
							{uniqBy(flatMap(study.entries, "studies"), "id").filter(
								(study: Study) => study && study.meta_analysis_data
							).length > 0 && (
								<div className="mt-1 text-center text-sm -tracking-1">
									{
										uniqBy(flatMap(study.entries, "studies"), "id").filter(
											(study: Study) => study && study.meta_analysis_data
										).length
									}{" "}
									{uniqBy(flatMap(study.entries, "studies"), "id").filter(
										(study: Study) => study.meta_analysis_data
									).length === 1
										? "Meta-analysis"
										: "Meta-analyses"}
								</div>
							)}
						</div>
					</StudySidebar>
				</TableCell>
				<TableCell></TableCell>
			</TableRow>

			{showEntries ? (
				<DatabaseEntries
					study={study}
					interventionOrTopic={interventionOrTopic}
					accessRules={accessRules}
				/>
			) : null}
		</>
	);
};

const calculateFreeAccess = (parentIndex: number, childCount: number): number => {
	if (parentIndex === 0) {
		if (childCount <= 2) return 1;
		return childCount <= 5 ? 2 : 3;
	}
	return childCount > 1 ? 1 : 0;
};

const debouncedTrackEdbSearch = debounce((value) => {
	window.mixpanel?.track("Search Examine database", { value });
}, 1500);

interface DatabaseBodyProps extends React.HTMLAttributes<HTMLDivElement> {
	data: any;
	entries?: { database_studies: Entry[] };
	pageGroup: "intervention" | "topic";
	page?: Page;
	pageType?:
		| "supplements"
		| "categories"
		| "conditions"
		| "outcomes"
		| "diets"
		| "others"
		| "foods";
	accessRules?: AccessRules;
}

export function DatabaseBody({
	data,
	entries,
	pageGroup,
	pageType,
	accessRules,
}: DatabaseBodyProps) {
	const searchParams = useSearchParams();
	const uniqueEntries = pageGroup === "intervention" ? entries?.database_studies : null;
	const [toggle, setToggle] = React.useState<boolean>(true);
	const [showConditions, setShowConditions] = React.useState<boolean>(true);
	const [keyword, setKeyword] = React.useState<string>("");
	const [filters, setFilters] = React.useState<{
		effect?: string[];
		grade?: string[];
		plusOnly?: boolean;
	}>(defaulFiltertValues);
	const [sort, setSort] = React.useState<{
		col: string;
		direction: "ASC" | "DESC";
	}>(null);

	const entriesOnly = !data?.database_studies?.length && entries.database_studies?.length;
	const showConditionsParam = searchParams.get("show_conditions") ?? "true";
	const hasAccess = useAccess("examine_database");
	const { user /*examine_database: hasAccess*/ } = accessRules;

	React.useEffect(() => {
		if (pageGroup !== "intervention") return;

		const storedConditions = localStorage.getItem("show_conditions");
		const showConditionsValue = entriesOnly
			? false
			: storedConditions !== null
			? JSON.parse(storedConditions)
			: showConditionsParam
			? JSON.parse(showConditionsParam)
			: null;

		setShowConditions(showConditionsValue);
		if (showConditionsValue !== null) {
			updateUrlParam("show_conditions", JSON.stringify(showConditionsValue));
		}
	}, [pageType, pageGroup, entriesOnly, showConditionsParam]);

	const handleCheckedChange = (checked: boolean) => {
		setShowConditions((prev) => !prev);
		localStorage.setItem("show_conditions", JSON.stringify(checked));
		updateUrlParam("show_conditions", JSON.stringify(checked));
	};

	React.useEffect(() => {
		if (!hasAccess) {
			setSort({ col: "name", direction: "ASC" });
			return;
		}

		if (pageGroup === "topic") {
			pageType === "outcomes"
				? setSort({ col: "name", direction: "ASC" })
				: setSort({ col: "intervention", direction: "ASC" });
			return;
		}

		setSort({ col: "grade", direction: "ASC" });
	}, [hasAccess, pageGroup, pageType]);

	const database = data?.database_studies;

	const filtered = React.useMemo(
		() =>
			showConditions && pageType !== "outcomes"
				? database
						?.sort((a: { [x: string]: { name: string } }, b: { [x: string]: { name: string } }) => {
							if (["condition", "intervention"].includes(sort?.col)) {
								const name1 = a[sort?.col].name.toLowerCase();
								const name2 = b[sort?.col].name.toLowerCase();
								if (sort?.direction === "ASC") {
									if (name1 < name2) {
										return -1;
									}
									if (name1 > name2) {
										return 1;
									}
								}

								if (sort?.direction === "DESC") {
									if (name1 > name2) {
										return -1;
									}
									if (name1 < name2) {
										return 1;
									}
								}

								return 0;
							}
						})
						?.map((study: any, idx: number) => {
							const searchTerm = keyword.toLowerCase();
							const conditionOrInterventionName = (
								study?.condition || study?.intervention
							)?.name?.toLowerCase();

							const filteredEntries = (
								conditionOrInterventionName?.includes(searchTerm)
									? study.entries
									: study.entries.filter((nestedEntry: any) =>
											nestedEntry.name.toLowerCase().includes(searchTerm)
									  )
							)
								?.filter((entry: Entry) => {
									if (!filters) return true; // Short-circuit filtering if no filters exist

									const normalizedEntryGrade = entry.grade.toLowerCase();
									const normalizedEffect = entry.arrow.text
										.toLowerCase()
										.replace("no effect", "no-effect")
										.replace("mixed effect", "mixed-effect")
										.split(" ")[0]; // Extract only the first word

									return (
										(!filters.grade?.length || filters.grade.includes(normalizedEntryGrade)) &&
										(!filters.effect?.length || filters.effect.includes(normalizedEffect))
									);
								})
								?.sort((a: Entry, b: Entry) => {
									if (sort?.direction === "ASC") {
										if (sort?.col === "arrow") {
											return getArrowScore(a.arrow.icon) > getArrowScore(b.arrow.icon) ? 1 : -1;
										} else {
											if (typeof a[sort?.col] === "string") {
												return a[sort?.col].toLowerCase() > b[sort?.col].toLowerCase() ? 1 : -1;
											}
											return a[sort?.col] > b[sort?.col] ? 1 : -1;
										}
									}

									if (sort?.direction === "DESC") {
										if (sort?.col === "arrow") {
											return getArrowScore(a.arrow.icon) < getArrowScore(b.arrow.icon) ? 1 : -1;
										} else {
											if (typeof a[sort?.col] === "string") {
												return a[sort?.col].toLowerCase() < b[sort?.col].toLowerCase() ? 1 : -1;
											}
											return a[sort?.col] < b[sort?.col] ? 1 : -1;
										}
									}
								})
								?.map((entry: Entry, i: number, _filteredEntries: Entry[]) => {
									const freeAccess = !hasAccess
										? !user && idx === 0
											? 2
											: user && idx === 0
											? 4
											: user && idx !== 0
											? 2
											: calculateFreeAccess(idx, _filteredEntries?.length)
										: 0;

									return {
										...entry,
										showContent: hasAccess || i < freeAccess,
										showTooltip: hasAccess || (freeAccess && i < 1),
									};
								});

							return {
								...study,
								entries: filteredEntries,
								total_entries: filteredEntries?.length,
							};
						})
						?.filter((study: any) => {
							const searchTerm = keyword.toLowerCase();
							const conditionOrInterventionName = (
								study?.condition || study?.intervention
							)?.name?.toLowerCase();

							return (
								study.entries?.length > 0 &&
								(conditionOrInterventionName?.includes(searchTerm) ||
									study.entries.some((entry: any) => entry.name.toLowerCase().includes(searchTerm)))
							);
						})
				: (pageType === "outcomes" ? database : uniqueEntries)
						?.filter((entry: Entry) => {
							if (!filters && !keyword) return true;

							const searchTerm = keyword.toLowerCase();
							const entryName = entry?.name?.toLowerCase();
							const normalizedEntryGrade = entry.grade.toLowerCase();
							const normalizedEffect = entry.arrow.text
								.toLowerCase()
								.replace("no effect", "no-effect")
								.replace("mixed effect", "mixed-effect")
								.split(" ")[0];

							return (
								// Keyword Search
								(!keyword || entryName?.includes(searchTerm)) &&
								// Filters
								(!filters ||
									((!filters.grade?.length || filters.grade.includes(normalizedEntryGrade)) &&
										(!filters.effect?.length || filters.effect.includes(normalizedEffect))))
							);
						})

						?.sort((a: Entry, b: Entry) => {
							if (sort?.direction === "ASC") {
								if (sort?.col === "arrow") {
									return getArrowScore(a.arrow.icon) > getArrowScore(b.arrow.icon) ? 1 : -1;
								} else {
									if (typeof a[sort?.col] === "string") {
										return a[sort?.col].toLowerCase() > b[sort?.col].toLowerCase() ? 1 : -1;
									}
									return a[sort?.col] > b[sort?.col] ? 1 : -1;
								}
							}

							if (sort?.direction === "DESC") {
								if (sort?.col === "arrow") {
									return getArrowScore(a.arrow.icon) < getArrowScore(b.arrow.icon) ? 1 : -1;
								} else {
									if (typeof a[sort?.col] === "string") {
										return a[sort?.col].toLowerCase() < b[sort?.col].toLowerCase() ? 1 : -1;
									}
									return a[sort?.col] < b[sort?.col] ? 1 : -1;
								}
							}
						})
						?.map((entry: Entry, i: number) => {
							return {
								...entry,
								showContent: !(!hasAccess && i > 2),
								showTooltip: !hasAccess && i >= 1 ? false : !(!hasAccess && i > 2),
							};
						}),
		[database, hasAccess, keyword, pageType, showConditions, uniqueEntries, user, sort, filters]
	);

	const toggleAll = (value: boolean) => {
		setToggle((prev) => value);
	};

	const resetFilters = () => {
		setFilters(defaulFiltertValues);
	};

	return (
		<div>
			{/* FILTERS */}
			<section className="mt-4 flex flex-wrap items-center justify-between gap-y-6">
				<div className="flex w-full flex-col flex-wrap items-stretch gap-4 sm:w-max sm:flex-row sm:items-center">
					<form
						className="relative overflow-hidden -tracking-2"
						onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
							e.preventDefault();
							setKeyword((e.target as HTMLFormElement)["search"].value);
						}}
					>
						<Input
							placeholder="Begin typing to filter database"
							className="size-full min-w-[20vw] px-3 py-2.5 text-sm text-black shadow-none lg:pl-4 lg:pr-20"
							type="search"
							name="search"
							onChange={(e) => {
								setKeyword(e.target.value);
								debouncedTrackEdbSearch(e.target.value);
							}}
						/>
						<Button
							type="submit"
							className="absolute right-0 top-0 h-full cursor-pointer rounded-l-none rounded-r-md bg-primary px-4 hover:bg-examine-purple-400"
						>
							<Search size="16" />
						</Button>
					</form>

					<div className="flex flex-row justify-between gap-4">
						{pageGroup === "intervention" && (
							<div className="flex items-center space-x-2">
								<Label htmlFor="entries-only" className="text-sm font-normal">
									Show Conditions
								</Label>
								<Switch
									id="entries-only"
									value={showConditions ? "on" : "off"}
									checked={showConditions}
									onCheckedChange={handleCheckedChange}
									disabled={!!entriesOnly}
								/>
							</div>
						)}
						<DatabaseFilter
							accessRules={accessRules}
							filters={filters}
							handleFilter={(_filters) => setFilters(_filters)}
						>
							<Button>
								<SlidersHorizontal size={16} />
								<span className="ml-1 py-1 text-sm">More Filters</span>
							</Button>
						</DatabaseFilter>
					</div>
				</div>

				{/* TODO: CONVERT TO COMPONENT */}
				{showConditions && pageType != "outcomes" && (
					<div className="flex">
						<Button
							variant="text"
							className="rounded text-gray-600 hover:bg-gray-100 hover:text-primary active:bg-gray-200"
							onClick={() => toggleAll(true)}
						>
							Expand All
							<ArrowDown size="18" className="ml-2" />
						</Button>
						<Button
							variant="text"
							className="rounded text-gray-600 hover:bg-gray-100 hover:text-primary active:bg-gray-200"
							onClick={() => toggleAll(false)}
						>
							Collapse All
							<ArrowUp size="18" className="ml-2" />
						</Button>
					</div>
				)}

				{filters &&
					(["a", "b", "c", "d", "f"].some((v) => !filters?.grade.includes(v)) ||
						["large", "moderate", "small", "mixed-effect", "no-effect"].some(
							(v) => !filters?.effect.includes(v)
						)) && (
						<div className="w-full space-y-3 rounded border border-sky-200 bg-sky-100 p-4">
							<div className="flex flex-row-reverse items-center justify-between">
								<Button
									variant="text"
									size="sm"
									className="col-span-2"
									onClick={() => resetFilters()}
								>
									<XCircle size="16" />
									Clear Filters
								</Button>
								<h6 className="text-sm">Filtered by:</h6>
							</div>
							<DatabaseFilter
								accessRules={accessRules}
								filters={filters}
								handleFilter={(_filters) => setFilters(_filters)}
							>
								<div className="flex flex-col flex-wrap gap-5">
									<div className="flex flex-wrap items-center justify-start gap-x-2">
										<p className="text-sm font-semibold">Grade: </p>
										<ul className="inline-flex flex-wrap gap-3">
											{filters?.grade
												?.filter((grade) => grade !== "all")
												?.map((grade) => (
													<li key={grade} className="cursor-pointer">
														<Label className="capitalize">{grade.toUpperCase()}</Label>
													</li>
												))}
										</ul>
									</div>
									<div className="flex items-baseline justify-start gap-x-2">
										<p className="text-sm font-semibold">Effect: </p>
										<ul className="inline-flex flex-wrap items-end gap-3">
											{filters?.effect
												?.filter((effect) => effect !== "all")
												?.map((effect) => (
													<li key={effect} className="cursor-pointer">
														<Label className="capitalize">{effect}</Label>
													</li>
												))}
										</ul>
									</div>
								</div>
							</DatabaseFilter>
						</div>
					)}
			</section>

			{/* DESKTOP EDB */}
			<article className="mt-4 hidden space-y-8 pb-16 sm:block">
				<Table variant="grid">
					<TableHeader
						className={cn(
							"grid text-menu",
							showConditions && pageType !== "outcomes" ? fiveColGrid : fourColGrid,
							filtered?.length < 1 ? "!hidden" : ""
						)}
					>
						<TableRow>
							{tableColumns[pageType === "outcomes" ? pageType : pageGroup]
								.filter((col) => showConditions || col.sort !== "condition")
								.map((col) => (
									<Tooltip key={col.sort} placement="bottom">
										<TooltipTrigger asChild>
											<TableHead
												key={col.title}
												className={cn(
													"flex-1 cursor-pointer border-none bg-primary text-menu font-normal text-white sm:p-0 lg:hover:bg-examine-purple-600"
												)}
											>
												<span className="px-6 py-8 lg:py-12">
													<span className="inline">{col.title}</span>
													{(col.rule === "free" || (col.rule === "paid" && hasAccess)) && (
														<Button
															variant="link"
															className="inline px-0 py-0 hover:scale-125"
															onClick={() =>
																setSort((prevSort) => ({
																	col: col.sort,
																	direction:
																		prevSort.col === col.sort
																			? prevSort.direction === "ASC"
																				? "DESC"
																				: "ASC"
																			: "DESC",
																}))
															}
														>
															{col?.sort && <Sort />}
														</Button>
													)}
												</span>
											</TableHead>
										</TooltipTrigger>
										<TooltipContent variant="purple" className="p-4 text-base">
											{parse(col?.content)}
										</TooltipContent>
									</Tooltip>
								))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{filtered?.length < 1 ? (
							<TableRow>
								<TableCell className="h-32 justify-center border-examine-purple-100 italic">
									No result found
								</TableCell>
							</TableRow>
						) : null}

						{showConditions && pageType !== "outcomes" ? (
							filtered?.map((study: any, id: any) => (
								<DatabaseRow
									study={study}
									interventionOrTopic={data[pageGroup]}
									toggle={toggle}
									key={id}
									accessRules={accessRules}
									viewport="desktop"
								/>
							))
						) : (
							<DatabaseEntries
								study={{
									[pageGroup]: { slug: data[pageGroup]?.slug, type: "no-parent" },
									entries: filtered,
								}}
								interventionOrTopic={data[pageGroup]}
								showConditions={false}
								type="desktop"
								accessRules={accessRules}
							/>
						)}
					</TableBody>
				</Table>
			</article>

			{/* MOBILE EDB */}
			<article className="mt-4 space-y-8 pb-16 sm:hidden">
				{showConditions && pageType !== "outcomes" ? (
					filtered?.map((study: any, id: any) => (
						<DatabaseRow
							key={id}
							study={study}
							interventionOrTopic={data[pageGroup]}
							toggle={toggle}
							accessRules={accessRules}
							viewport="mobile"
							pageType={pageType}
						/>
					))
				) : (
					<DatabaseEntries
						study={{
							[pageGroup]: { slug: data[pageGroup]?.slug, type: "no-parent" },
							entries: filtered,
						}}
						interventionOrTopic={data[pageGroup]}
						showConditions={false}
						accessRules={accessRules}
						type="mobile"
						pageType={pageType}
					/>
				)}

				{filtered?.length < 1 ? (
					<div className="flex h-24 items-center justify-center border-examine-purple-100 italic">
						No result found
					</div>
				) : null}
			</article>
		</div>
	);
}

interface ExamineDatabaseProps extends React.HTMLAttributes<HTMLDivElement> {
	page: Page;
	hasDatabase?: boolean;
	accessRules?: AccessRules;
	asAccordion?: boolean;
}

interface Plan {
	title: string;
	price: number;
	description: string;
	cta: string;
	url: string;
	features: (string | string[])[];
}

const plans: Plan[] = [
	{
		title: "Examine+ EDB",
		price: 90,
		description: "Full database access",
		cta: "Start Now",
		url: "/checkout/ZQGr52Y/",
		features: [
			"Examine Database",
			[
				"Unlimited access",
				"Dosages and How to Take",
				"All evidence grades",
				"26 Categories",
				"500+ Supplements & interventions",
				"500+ Conditions & goals",
				"1000+ Health outcomes",
			],
			"Research Feed",
			["2 Study Summaries per day"],
			"Supplement Guides",
			["Full access to the Healthy Aging & Longevity Guide", "Limited access to 16 other guides"],
			"Member Newsletters",
			[
				"Weekly insights on health and nutrition",
				"Shape Examine+ by sharing feedback with the team",
			],
		],
	},
	{
		title: "Examine+",
		price: 198,
		description: "Unlock all of Examine",
		cta: "Try free for 7 days",
		url: "/checkout/jplqpJ6/",
		features: [
			"Get everything in Examine+ EDB and...",
			"ExamineAI",
			[
				"Quickly and easily get answers to your health questions",
				"Search over 25 million words of Examine content in seconds",
				"Rest easy with human-verified content: ExamineAI only retrieves content already interpreted, contextualized, and summarized by our team of experts",
			],
			"Research Feed",
			[
				"Unlimited Study Summaries (150+ published monthly)",
				"In-depth analysis and commentary with Editor's Picks",
				"Downloadable PDFs",
				"Customizable categories",
				"Emailed daily/weekly/monthly",
			],
			"Supplement Guides",
			[
				"Unlimited access to 17 popular conditions and goals",
				"Combos, timing, best uses",
				"Step-by-step guidance",
				"Downloadable PDFs",
			],
			"Continuing Education Credits",
			["Earn professional credits at no extra charge for AND (CDR), NASM, and NSCA"],
		],
	},
];

const PlanCard = ({ plan, onCtaClick }: { plan: Plan; onCtaClick?: () => void }) => {
	const [isExpanded, setIsExpanded] = React.useState(false);
	const planRef = React.useRef<HTMLDivElement>(null);
	useTrackEdbElement({ element: planRef });

	return (
		<Card
			ref={planRef}
			key={plan.title}
			className={cn(
				"relative overflow-hidden rounded-2xl border border-[#e2e2e2] p-1.5 md:max-h-none ",
				!isExpanded && "max-h-[380px]"
			)}
		>
			<div
				className={cn(
					"h-full gradient-mask-b-60 md:gradient-mask-none",
					isExpanded && "!gradient-mask-none"
				)}
			>
				<CardHeader className="pb-5 font-semibold leading-tight">
					<CardTitle className="text-25 text-examine-purple-600">{plan.title}</CardTitle>
					<CardDescription className="!mt-5">
						<span className="text-40">${plan.price}</span> <span className="text-25">USD</span>{" "}
						<span className="text-30">/yr</span>
					</CardDescription>
					<p className="text-primary">{plan.description}</p>
				</CardHeader>
				<CardContent>
					<Link
						href={plan.url}
						className={cn(buttonVariants({ size: "lg" }), "w-full justify-center font-bold")}
						onClick={onCtaClick}
					>
						{plan.cta}
					</Link>

					<p className="mt-2.5 text-center text-xs font-bold text-examine-purple-700">
						60-day money-back guarantee
					</p>
					<ul className="mt-5 leading-tight text-examine-purple-700">
						{plan.features.map((item, idx) =>
							typeof item === "string" ? (
								<li key={idx} className="mt-[19px] font-bold">
									{item}
								</li>
							) : (
								<ul className="ml-4 list-disc" key={idx}>
									{item.map((subItem, subIndex) => (
										<li key={subIndex}>{subItem}</li>
									))}
								</ul>
							)
						)}
					</ul>
				</CardContent>
			</div>
			<div
				className={cn(
					"absolute bottom-2.5 left-0 right-0 flex items-center justify-center py-0.5",
					isExpanded ? "hidden" : "md:hidden"
				)}
			>
				<Button
					variant="text"
					onClick={() => setIsExpanded(!isExpanded)}
					className="flex items-center gap-2.5 p-0 font-bold"
				>
					More
					<ChevronDown className={cn("h-5 w-5", isExpanded && "rotate-180")} />
				</Button>
			</div>
		</Card>
	);
};

const EDBSlideout = ({ children: trigger }: { children: React.ReactNode }) => {
	return (
		<Sheet>
			<SaleWrapper
				cta={
					<SheetTrigger asChild onClick={sendViewSlideoutEvent}>
						{trigger}
					</SheetTrigger>
				}
			>
				{trigger}
			</SaleWrapper>
			<SheetContent className="z-[10000001] grid gap-[30px] p-6 sm:max-w-none">
				<SheetClose asChild>
					<Button
						variant="text"
						className="absolute right-1 top-0 md:right-2 md:top-4"
						aria-label="Close"
					>
						<X size={30} />
					</Button>
				</SheetClose>
				<SheetTitle className="mt-1.5 text-center text-base font-bold leading-tight text-examine-purple-700 md:mt-0 md:!text-25">
					Get all the grades with an all-access subscription.
				</SheetTitle>
				<div className="grid grid-cols-1 gap-[30px] md:grid-cols-2">
					{plans.map((plan) => (
						<PlanCard
							plan={plan}
							key={plan.url}
							onCtaClick={() =>
								trackAdClick({ location: `EDB Slideout - ${plan.title}`, label: plan.cta })
							}
						/>
					))}
				</div>
				<div className="text-center text-base font-bold leading-tight text-examine-purple-700 md:!text-25">
					No ads. No sponsors. No bias. 100% reader-supported.
				</div>
			</SheetContent>
		</Sheet>
	);
};

const EDBOptin = ({ isLoggedIn }: { isLoggedIn?: boolean }) => {
	const hasAccess = useAccess("examine_database");

	return (
		<>
			{isLoggedIn ? (
				<>Get the full database with a</>
			) : (
				<>
					Unlock more grades with a{" "}
					<Link
						href="/signup/"
						onClick={() => sendFreeAccountClickEvent({ isLoggedIn })}
						className="edb-signup text-examine-purple-400 underline"
					>
						free account
					</Link>{" "}
					or get the full database with a
				</>
			)}{" "}
			<EDBSlideout>
				<span>
					<span
						onClick={() => sendPaidSubscriptionClickEvent({ isLoggedIn, hasAccess })}
						className="edb-signup cursor-pointer font-semibold text-examine-purple-400 underline"
					>
						paid subscription
					</span>
					.
				</span>
			</EDBSlideout>
		</>
	);
};

const EDBSectionSalesAlert = () => {
	return (
		<ExaminePlusLink
			trackDetails={{
				trackFor: "sale",
				location: "Above Examine Database",
				label: "Unlock the entire Examine Database and more!",
			}}
		>
			<Alert
				variant="info"
				id={`edbSalesAlert-${salesId}${salesYear}`}
				className="mt-4"
				allowDismiss
			>
				<AlertTitle className="text-center font-semibold text-primary underline">
					Unlock the entire Examine Database and more!
				</AlertTitle>
				<AlertDescription>
					<ul className="mx-auto">
						<li>
							Access our database of 10,000+ studies and{" "}
							<strong>stay on top of the latest health and nutrition information</strong>.
						</li>
						<li>
							<Badge variant="premium" size="sm" className="inline">
								New!
							</Badge>{" "}
							<strong>Get answers in seconds with ExamineAI</strong>, our large language model
							trained exclusively on published Examine content.
						</li>
						<li>
							Unlock <strong>graded evidence</strong> for 500+ interventions, 400+ conditions, and
							1,000+ health outcomes.
						</li>
						<li>
							<strong>Save time and money</strong> by focusing only on the supplements and
							interventions that work.
						</li>
						<li>
							Get <strong>daily summaries of the latest evidence</strong>, written, reviewed, and
							categorized by our research team of MDs, RDs, PharmDs, and more.
						</li>
						<li>
							<strong>No ads, no sponsors, no bias</strong> — just evidence that helps you be
							healthier.
						</li>
					</ul>
				</AlertDescription>
				<FlashSaleCTA />
			</Alert>
		</ExaminePlusLink>
	);
};

const EDBSectionOptinAlert = ({ accessRules }: { accessRules: AccessRules }) => {
	return (
		<AccessCheck feature={["examine_database"]}>
			<Alert id="linkedToDatabase" variant="info" className="mt-4">
				<EDBOptin isLoggedIn={!!accessRules.user} />
			</Alert>
		</AccessCheck>
	);
};

const EDBSectionAlert = ({ cta }: { cta: React.ReactNode }) => {
	return (
		<SaleWrapper cta={cta}>
			<EDBSectionSalesAlert />
		</SaleWrapper>
	);
};

export function ExamineDatabase({
	page,
	children,
	hasDatabase,
	accessRules,
	asAccordion = true,
}: ExamineDatabaseProps) {
	const contentRef = React.useRef<HTMLDivElement>(null);
	const hasAccess = useAccess("examine_database");

	let childProps: DatabaseBodyProps | undefined;

	React.Children.forEach(children, (child) => {
		if (React.isValidElement(child)) {
			childProps = child.props;
		}
	});

	useTrackEdbElement({ element: contentRef, condition: !hasAccess });

	if (!hasDatabase || (!childProps?.data && childProps?.page?.type !== "categories")) return null;

	if (asAccordion) {
		return (
			<Accordion type="single" id="examine-database" defaultValue="examine-database" collapsible>
				<AccordionItem value="examine-database">
					<AccordionTrigger
						as="h2"
						copyLinkUrl={page?.isSplitTest ? `${page?.url}` : `${page?.url}/#examine-database`}
						className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
						asChild
					>
						<div className="flex flex-wrap items-center gap-2">
							Examine Database: {page?.name}
							<Tooltip>
								<TooltipTrigger asChild>
									<Info strokeWidth={1.5} size={16} className="mt-1.5 font-sans" />
								</TooltipTrigger>
								<TooltipContent>
									<p>
										The Examine Database tells you what works and what doesn&apos;t, according to
										the available randomized trials. Individual results will vary given
										physiological complexity, as well as varying trial quality, methodology, and
										sampling.
									</p>
								</TooltipContent>
							</Tooltip>
							<span className="hidden items-center md:flex">
								{hasAccess ? (
									<PaidPill />
								) : (
									<>
										<LockBadge
											size={9}
											tooltip={<OverlayCta location={`${page?.name} ED label`} trackEdbSignup />}
											className="hidden align-top md:inline-flex"
										/>
									</>
								)}
							</span>
						</div>
					</AccordionTrigger>
					<AccordionContent ref={contentRef}>
						<EDBSectionAlert cta={<EDBSectionOptinAlert accessRules={accessRules} />} />
						{children}
						<AccessCheck feature={["examine_database"]}>
							<WhatWorksCta
								trackDetails={{
									location: `Below EDB On ${page?.name}`,
								}}
							/>
						</AccessCheck>
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		);
	}

	return (
		<>
			<EDBSectionAlert cta={<EDBSectionOptinAlert accessRules={accessRules} />} />
			{children}
			<AccessCheck feature={["examine_database"]}>
				<WhatWorksCta
					trackDetails={{
						location: `Below EDB On ${page?.name}`,
					}}
				/>
			</AccessCheck>
		</>
	);
}
